import React from 'react';
import { CheckCircle, Clock, Loader2, AlertCircle } from 'lucide-react';

interface ProcessingStatusProps {
  currentStep: number;
  totalSteps: number;
  progress: number;
  estimatedTime: number;
  error?: string;
  onCancel: () => void;
  onRetry?: () => void;
}

const steps = [
  { id: 1, title: 'Analyzing Video', description: 'Extracting video metadata and content' },
  { id: 2, title: 'Processing Audio', description: 'Converting speech to text' },
  { id: 3, title: 'Generating Summary', description: 'Creating intelligent summary' },
  { id: 4, title: 'Creating Transcript', description: 'Finalizing downloadable content' },
];

export const ProcessingStatus: React.FC<ProcessingStatusProps> = ({
  currentStep,
  totalSteps,
  progress,
  estimatedTime,
  error,
  onCancel,
  onRetry,
}) => {
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-xl border border-red-200 p-8">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Processing Failed</h3>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="flex justify-center space-x-4">
              {onRetry && (
                <button
                  onClick={onRetry}
                  className="px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors"
                >
                  Try Again
                </button>
              )}
              <button
                onClick={onCancel}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors"
              >
                Start Over
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
        <div className="text-center mb-8">
          <h3 className="text-3xl font-bold text-gray-900 mb-2">Processing Your Video</h3>
          <p className="text-gray-600">
            Estimated time remaining: <span className="font-semibold text-blue-600">{formatTime(estimatedTime)}</span>
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Overall Progress</span>
            <span className="text-sm font-medium text-blue-600">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-gradient-to-r from-blue-500 to-green-500 h-3 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-4 mb-8">
          {steps.map((step) => (
            <div key={step.id} className="flex items-center space-x-4 p-4 rounded-xl bg-gray-50">
              <div className="flex-shrink-0">
                {step.id < currentStep ? (
                  <CheckCircle className="w-6 h-6 text-green-500" />
                ) : step.id === currentStep ? (
                  <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                ) : (
                  <Clock className="w-6 h-6 text-gray-400" />
                )}
              </div>
              <div className="flex-1">
                <h4 className={`font-semibold ${
                  step.id <= currentStep ? 'text-gray-900' : 'text-gray-500'
                }`}>
                  {step.title}
                </h4>
                <p className={`text-sm ${
                  step.id <= currentStep ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  {step.description}
                </p>
              </div>
              {step.id === currentStep && (
                <div className="flex-shrink-0">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Cancel Button */}
        <div className="text-center">
          <button
            onClick={onCancel}
            className="px-6 py-3 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel Processing
          </button>
        </div>
      </div>
    </div>
  );
};